import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Plus, Save } from "lucide-react";
import api from "../lib/api";

const emptyReview = { customer_name: "", rating: 5, text: "", vehicle_purchased: "", is_featured: false, is_published: true };

const AdminReviews = () => {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyReview);

  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (id, patch) => {
    try { await api.put(`/admin/reviews/${id}`, patch); load(); toast.success("Saved"); }
    catch { toast.error("Failed"); }
  };
  const create = async () => {
    if (!draft.customer_name || !draft.text) { toast.error("Name and text required"); return; }
    try { await api.post("/admin/reviews", draft); setDraft(emptyReview); setCreating(false); load(); toast.success("Added"); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/admin/reviews/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline text-[#7A1519]">Reviews</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold">Customer Reviews</h1>
        </div>
        <button onClick={() => setCreating(!creating)} className="bg-[#7A1519] text-white px-4 py-2.5 rounded-sm text-sm inline-flex items-center gap-2" data-testid="rev-add-toggle">
          <Plus size={14} /> {creating ? "Cancel" : "Add Review"}
        </button>
      </div>

      {creating && (
        <div className="bg-white border hairline p-6 rounded-sm space-y-3" data-testid="rev-new">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Customer name" value={draft.customer_name} onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })} className="bg-[#F7F6F3] border hairline px-3 py-2 rounded-sm text-sm" data-testid="rev-name" />
            <input placeholder="Vehicle purchased" value={draft.vehicle_purchased} onChange={(e) => setDraft({ ...draft, vehicle_purchased: e.target.value })} className="bg-[#F7F6F3] border hairline px-3 py-2 rounded-sm text-sm" data-testid="rev-vehicle" />
          </div>
          <textarea rows={3} placeholder="Review text" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} className="w-full bg-[#F7F6F3] border hairline px-3 py-2 rounded-sm text-sm" data-testid="rev-text" />
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2"><span className="overline text-[#242424]/60">Rating</span>
              <select value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: Number(e.target.value) })} className="bg-white border hairline px-2 py-1 rounded-sm">
                {[5,4,3,2,1].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={draft.is_featured} onChange={(e) => setDraft({ ...draft, is_featured: e.target.checked })} /> Featured</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={draft.is_published} onChange={(e) => setDraft({ ...draft, is_published: e.target.checked })} /> Published</label>
            <button onClick={create} className="ml-auto bg-[#111111] text-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2" data-testid="rev-save-new"><Save size={12} /> Save</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="bg-white border hairline p-5 rounded-sm" data-testid={`rev-item-${r.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="font-medium">{r.customer_name}</div>
                  <div className="flex gap-0.5 text-[#7A1519]">
                    {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                  </div>
                </div>
                {r.vehicle_purchased && <div className="text-xs text-[#242424]/60 mt-0.5">{r.vehicle_purchased}</div>}
                <p className="text-sm mt-2">{r.text}</p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={r.is_featured} onChange={(e) => save(r.id, { is_featured: e.target.checked })} /> Featured</label>
                <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={r.is_published} onChange={(e) => save(r.id, { is_published: e.target.checked })} /> Published</label>
                <button onClick={() => del(r.id)} className="text-[#7A1519] text-xs inline-flex items-center gap-1 mt-2" data-testid={`rev-del-${r.id}`}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminReviews;
